/* Breeo tabbed modules (Collection tabs + Story slider) */
(() => {
  if (customElements.get('breeo-tabs')) return;

  class BreeoTabs extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll(':scope [role="tab"]'));
      this.panels = Array.from(this.querySelectorAll(':scope [role="tabpanel"]'));

      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.select(tab.getAttribute('aria-controls')));
        tab.addEventListener('keydown', (event) => this.onKeydown(event, tab));
      });

      /* Side "peek" images (story module) jump to their tab */
      this.addEventListener('click', (event) => {
        const peek = event.target.closest('[data-tab-target]');
        if (peek && this.contains(peek)) this.select(peek.getAttribute('data-tab-target'));
      });

      this.initArrows();
      this.syncMedia();
    }

    /* Collection-slider (V2) arrows. We drive the overflow container directly with live
       measurements — the theme's own scroll-carousel/next-button init on page load and break
       when the section ships hidden (display:none), so we don't rely on them. */
    initArrows() {
      this.querySelectorAll('.bct-carousel').forEach((wrap) => {
        const area = wrap.querySelector('.scroll-area');
        const prev = wrap.querySelector('.bct-arrow--prev');
        const next = wrap.querySelector('.bct-arrow--next');
        if (!area || (!prev && !next)) return;

        const step = () => Math.max(area.clientWidth * 0.85, 260);
        const update = () => {
          const max = area.scrollWidth - area.clientWidth - 2;
          if (prev) prev.disabled = area.scrollLeft <= 2;
          if (next) next.disabled = area.scrollLeft >= max;
        };
        if (prev) prev.addEventListener('click', () => area.scrollBy({ left: -step(), behavior: 'smooth' }));
        if (next) next.addEventListener('click', () => area.scrollBy({ left: step(), behavior: 'smooth' }));
        area.addEventListener('scroll', () => {
          if (this._arrowRaf) return;
          this._arrowRaf = requestAnimationFrame(() => { this._arrowRaf = null; update(); });
        }, { passive: true });
      });
    }

    onKeydown(event, tab) {
      if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
      event.preventDefault();
      const index = this.tabs.indexOf(tab);
      const offset = event.key === 'ArrowRight' ? 1 : -1;
      const next = this.tabs[(index + offset + this.tabs.length) % this.tabs.length];
      next.focus();
      this.select(next.getAttribute('aria-controls'));
    }

    select(panelId) {
      if (!panelId) return;

      this.tabs.forEach((tab) => {
        const active = tab.getAttribute('aria-controls') === panelId;
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
        tab.toggleAttribute('data-active', active);
      });

      this.panels.forEach((panel) => {
        const active = panel.id === panelId;
        panel.toggleAttribute('hidden', !active);
      });

      this.syncMedia();
    }

    syncMedia() {
      this.panels.forEach((panel) => {
        panel.querySelectorAll('video').forEach((video) => {
          if (panel.hasAttribute('hidden')) {
            video.pause();
            return;
          }

          if (!video.muted) return;

          /* The browser can drop a play() that races the panel unhide, so retry once it settles. */
          const attempt = () => {
            const playPromise = video.play();
            if (playPromise) playPromise.catch(() => {});
          };

          attempt();
          setTimeout(() => {
            if (video.paused && !panel.hasAttribute('hidden')) attempt();
          }, 150);
        });
      });
    }
  }

  customElements.define('breeo-tabs', BreeoTabs);
})();

/* Story module: center-mode carousel with dimmed neighbor slides + synced heading/tabs.
   Wraps around — clones of the outer slides sit at each end and teleport to the real
   slide once scrolling settles, so the last chapter peeks left of the first.
   Video playback is left entirely to the theme's <video-media autoplay> in-view player;
   we never pause/play videos ourselves (doing so fought the theme and greyed out clips). */
(() => {
  if (customElements.get('breeo-story-slider')) return;

  class BreeoStorySlider extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.bss-track');
      this.slides = Array.from(this.track.querySelectorAll('.bss-slide'));
      this.tabs = Array.from(this.querySelectorAll('.bss-tab'));
      this.headings = Array.from(this.querySelectorAll('.bss-heading'));
      this.activeIndex = 0;

      if (this.slides.length > 1 && !this.track.querySelector('[data-clone]')) {
        this.track.prepend(this.makeClone(this.slides[this.slides.length - 1]));
        this.track.append(this.makeClone(this.slides[0]));
      }
      this.allSlides = Array.from(this.track.querySelectorAll('.bss-slide'));

      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.goTo(Number(tab.dataset.index)));
        tab.addEventListener('keydown', (event) => this.onKeydown(event));
      });

      /* Clicking any dimmed slide (clones included) slides over to it */
      this.track.addEventListener('click', (event) => {
        const slide = event.target.closest('.bss-slide');
        if (!slide || slide.classList.contains('is-active')) return;
        this.centerOn(slide, true);
        this.setActive(Number(slide.dataset.index));
      });

      this.track.addEventListener('scroll', () => {
        if (!this._raf) {
          this._raf = requestAnimationFrame(() => {
            this._raf = null;
            this.setActive(Number(this.nearestSlide().dataset.index));
          });
        }
        clearTimeout(this._settleDebounce);
        this._settleDebounce = setTimeout(() => this.teleportIfClone(), 150);
      }, { passive: true });

      /* A user gesture takes over — stop the centering nudge so we never fight a swipe */
      ['wheel', 'touchstart', 'pointerdown'].forEach((eventName) => {
        this.track.addEventListener(eventName, () => clearInterval(this._settleTimer), { passive: true });
      });

      /* Keep the active slide centered through viewport changes */
      window.addEventListener('resize', () => {
        clearTimeout(this._resizeTimer);
        this._resizeTimer = setTimeout(() => this.goTo(this.activeIndex, false), 150);
      });

      /* Center the first slide once layout settles */
      requestAnimationFrame(() => this.goTo(0, false));
    }

    makeClone(slide) {
      const clone = slide.cloneNode(true);
      clone.removeAttribute('id');
      clone.removeAttribute('role');
      clone.removeAttribute('aria-labelledby');
      clone.removeAttribute('data-shopify-editor-block');
      clone.setAttribute('data-clone', '');
      clone.setAttribute('aria-hidden', 'true');
      clone.classList.remove('is-active');
      clone.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
      /* Reset any player state copied from the live element so the clone's <video-media>
         re-initializes its own autoplay/in-view handling (otherwise it stays greyed). */
      clone.querySelectorAll('video-media').forEach((vm) => {
        ['loaded', 'can-play', 'playing', 'suspended'].forEach((attr) => vm.removeAttribute(attr));
      });
      return clone;
    }

    onKeydown(event) {
      if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
      event.preventDefault();
      const offset = event.key === 'ArrowRight' ? 1 : -1;
      const next = (this.activeIndex + offset + this.slides.length) % this.slides.length;
      this.tabs[next].focus();
      this.goTo(next);
    }

    nearestSlide() {
      const center = this.track.scrollLeft + this.track.clientWidth / 2;
      let best = this.allSlides[0];
      let bestDistance = Infinity;
      this.allSlides.forEach((slide) => {
        const distance = Math.abs(slide.offsetLeft + slide.offsetWidth / 2 - center);
        if (distance < bestDistance) {
          bestDistance = distance;
          best = slide;
        }
      });
      return best;
    }

    /* Once scrolling settles on a clone, jump to its real twin. Clearing the centering
       nudge first is the fix for the old "bounce back to the first slide" loop — without
       it the nudge interval kept re-targeting the clone and fought this teleport. */
    teleportIfClone() {
      clearInterval(this._settleTimer);
      const slide = this.nearestSlide();
      if (!slide.hasAttribute('data-clone')) return;
      const real = this.slides[Number(slide.dataset.index)];
      this.track.scrollTo({ left: this.track.scrollLeft + (real.offsetLeft - slide.offsetLeft), behavior: 'instant' });
    }

    centerOn(slide, smooth = true) {
      const target = () => slide.offsetLeft - (this.track.clientWidth - slide.offsetWidth) / 2;
      this.track.scrollTo({ left: target(), behavior: smooth ? 'smooth' : 'instant' });

      clearInterval(this._settleTimer);
      if (smooth) {
        let attempts = 0;
        this._settleTimer = setInterval(() => {
          attempts += 1;
          const distance = Math.abs(this.track.scrollLeft - target());
          if (distance < 8 || attempts > 5) {
            clearInterval(this._settleTimer);
            if (distance >= 8 && attempts > 5) this.track.scrollTo({ left: target(), behavior: 'instant' });
          } else if (attempts > 1) {
            this.track.scrollTo({ left: target(), behavior: 'smooth' });
          }
        }, 400);
      }
    }

    goTo(index, smooth = true) {
      const slide = this.slides[index];
      if (!slide) return;
      this.centerOn(slide, smooth);
      this.setActive(index);
    }

    setActive(index) {
      const changed = index !== this.activeIndex;
      this.activeIndex = index;

      this.allSlides.forEach((slide) => slide.classList.toggle('is-active', Number(slide.dataset.index) === index));
      if (!changed) return;

      this.headings.forEach((heading, i) => heading.classList.toggle('is-active', i === index));
      this.tabs.forEach((tab, i) => tab.setAttribute('aria-selected', i === index ? 'true' : 'false'));
    }
  }

  customElements.define('breeo-story-slider', BreeoStorySlider);
})();
